const SearchQuery = require("./searchquery").SearchQuery;
/**
 * @typedef {Object} PolylineFilter
 * @property {String} encodedPolyline a polyline encoded to the google format
 * @see [polylineutility](https://developers.google.com/maps/documentation/utilities/polylineutility)
 * @property {Number} [radius] max distance to retrieve store from the polyline (in meter).
 * Default value is 10000
 */

/**
 * @typedef {Object} SearchParametersOptions
 * @property {Number} [lat] latitude of the position to search nearest stores.
 * @property {Number} [lng] longitude of the position to search nearest stores.
 * @property {Number} [maxDistanceFromUser] maximum distance from user (in meter).
 * @property {Number} [maxStores] maximum number of stores to return (deprecated use pagination instead).
 * @property {woosmap.QuerySearchOptions} [searchTextOptions] options to filter on store's text properties.
 * @property {woosmap.query.Query|woosmap.query.Field|String} [query] search query that replaces the searchTextOptions.
 * @property {PolylineFilter} [polylineFilter] to filter on polyline path.
 * @property {Number} [page] the request page
 * @property {Number} [storesByPage] the number of store by page (default:100, max:300)
 * @property {google.maps.LatLngBounds|google.maps.LatLngBoundsLiteral} [bounds] Bounds in which
 * the search will be performed
 * @property {woosmap.search.SearchParameters.GroupByValues} [groupBy] Aggregate stores by type or tag.
 * Only available when using DataSource.countStoresByParameters method
 * @property {Boolean} [zone] whether the search should perform an intersection between point and zones.
 */

/**
 * @memberOf woosmap.search
 * @since 1.2.0
 */
class SearchParameters {
    /**
     * Constructs a new SearchParameters
     * @param {SearchParametersOptions} [searchParametersOptions]
     * @constructor
     */
    constructor(searchParametersOptions) {
        const searchOptionsInit = searchParametersOptions || {};
        /**
         * Latitude of the position to search nearest stores
         * @type {Number}
         */
        this.lat = searchOptionsInit.lat;
        /**
         * Longitude of the position to search nearest stores.
         * @type {Number}
         */
        this.lng = searchOptionsInit.lng;
        /**
         * Maximum number of stores to return.
         * @type {Number}
         * @deprecated
         */
        this.maxStores = searchOptionsInit.maxStores;
        /**
         * Maximum distance from user (in meter).
         * @type {Number}
         */
        this.maxDistanceFromUser = searchOptionsInit.maxDistanceFromUser;
        /**
         * Query to filter on store's properties (text, types and tags).
         * @deprecated
         * @type {woosmap.search.SearchQuery}
         */
        this.searchQuery = new SearchQuery(searchOptionsInit.querySearchOptions);

        /**
         * @type {woosmap.query.Query|woosmap.query.Field|String}
         */
        this.query = searchOptionsInit.query;

        /**
         * PolylineFilter to filter on polyline path
         * @type {PolylineFilter}
         */
        this.polylineFilter = searchOptionsInit.polylineFilter;

        /**
         * The expected page
         * @type {Number}
         */
        this.page = searchOptionsInit.page;

        /**
         * Number of store in one page (default: 100, max: 300)
         * @type {Number}
         */
        this.storesByPage = searchOptionsInit.storesByPage;

        /**
         * Count the stores by group values
         * @type {woosmap.search.SearchParameters.GroupByValues}
         */
        this.groupBy = searchOptionsInit.groupBy;

        /**
         * Bounds in which the search will be performed
         * @type {google.maps.LatLngBoundsLiteral}
         */
        this.bounds = searchOptionsInit.bounds;
        this.bounds = this.bounds?.toJSON ? this.bounds.toJSON() : this.bounds;

        /**
         * Whether the search should perform an intersection between point and zones.
         * @type {Boolean}
         */
        this.zone = searchOptionsInit.zone;
    }

    _boundsToLitteral(bounds) {
        return bounds?.toJSON ? bounds.toJSON() : bounds;
    }

    /**
     * Build encoded parameters of an url to search stores (&max_number=3).
     * @returns {String}
     */
    buildUrl() {
        let urlParameters = "";
        if (this.lat !== undefined || this.lng !== undefined) {
            urlParameters += `&lat=${this.lat}&lng=${this.lng}`;
            if (this.maxDistanceFromUser) {
                urlParameters += `&max_distance=${this.maxDistanceFromUser}`;
            }
        }
        if (this.maxStores && !this.storesByPage) {
            if (console?.error) {
                console.error("maxStores is deprecated please use pagination options instead (storesByPage and page)");
            }
            urlParameters += `&stores_by_page=${this.maxStores}`;
            //TODO remove the below line when the locastore prod is deploy
            urlParameters += `&limit=${this.maxStores}`;
        }
        if (this.storesByPage) {
            urlParameters += `&stores_by_page=${this.storesByPage}`;
            //TODO remove the below line when the locastore prod is deploy
            urlParameters += `&limit=${this.storesByPage}`;
        }
        if (this.page) {
            urlParameters += `&page=${this.page}`;
        }
        if (this.query) {
            urlParameters += `&query=${encodeURIComponent(this.query.toString())}`;
        } else if (this.searchQuery) {
            if (this.searchQuery.stringify()) {
                console.error("searchQuery is deprecated use query parameter instead.");
                urlParameters += `&query=${encodeURIComponent(this.searchQuery.stringify())}`;
            }
        }

        if (this.zone !== undefined) {
            urlParameters += `&zone=${encodeURIComponent(this.zone)}`;
        }

        if (
            this.polylineFilter &&
            this.polylineFilter.encodedPolyline !== undefined &&
            this.polylineFilter.radius !== undefined
        ) {
            urlParameters += `&polyline=${encodeURIComponent(this.polylineFilter.encodedPolyline)}`;
            urlParameters += `&radius=${this.polylineFilter.radius}`;
        }

        if (this.bounds) {
            urlParameters += `&bounds=${encodeURIComponent(
                `${this.bounds.west},${this.bounds.south},${this.bounds.east},${this.bounds.north}`,
            )}`;
        }

        if (this.groupBy) {
            urlParameters += `&group_by=${encodeURIComponent(this.groupBy)}`;
        }

        return urlParameters;
    }
}

/**
 * Allowed values for the groupBy parameter.
 * @enum {String}
 * @memberOf woosmap.search
 */
SearchParameters.GroupByValues = {
    /**
     * Group By Type
     */
    TYPE: "type",
    /**
     * Group By Tags
     */
    TAG: "tag",
};

exports.SearchParameters = SearchParameters;
